import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminPrivacy.css';   // reuse shared admin legal-page styles
import { motion, AnimatePresence } from 'framer-motion';
import { FaSave, FaPlus, FaTrash, FaRegFileAlt, FaHeading, FaParagraph, FaListUl } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const API_URL = 'https://aayushrealproperties.in/nimaibackend/public/api/refund-policies';

const AdminRefundPolicy = () => {
    const [isSaving,   setIsSaving]   = useState(false);
    const [refundId,   setRefundId]   = useState(null);
    const [refundData, setRefundData] = useState({
        hero: {
            titlePart:   'Our',
            highlight:   'Refund',
            suffix:      'Policy',
            description: 'We are committed to fair and transparent billing. Read our complete refund and cancellation policy before engaging our services.',
        },
        sections: [
            {
                id:      Date.now(),
                icon:    'shake',
                title:   'Nature of Our Services',
                content: 'Nimai Solutions provides custom software development and digital services. All deliverables are tailor-made and irreversibly customised to each client.',
                points:  [],
            },
        ],
        lastUpdated: 'June 1, 2026',
    });

    useEffect(() => { fetchRefund(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const authHeaders = () => ({
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        'Accept': 'application/json',
    });

    const fetchRefund = async () => {
        try {
            const { ok, data: result } = await safeFetch(API_URL, { headers: authHeaders() });
            let item = null;
            if (Array.isArray(result))     item = result[0];
            else if (result?.data)         item = Array.isArray(result.data) ? result.data[0] : result.data;
            if (ok && item) {
                setRefundId(item.id);
                setRefundData({
                    hero: {
                        titlePart:   item.hero_title_part1    || 'Our',
                        highlight:   item.hero_highlight_word || 'Refund',
                        suffix:      item.hero_title_suffix   || 'Policy',
                        description: item.hero_description    || '',
                    },
                    sections: (item.sections || []).map((s, idx) => ({
                        id:      s.id   || idx,
                        icon:    s.icon_type,
                        title:   s.section_title,
                        content: s.content,
                        points:  s.key_points || [],
                    })),
                    lastUpdated: item.last_updated_date || 'June 1, 2026',
                });
            }
        } catch (err) {
            console.error('Error fetching refund policy:', err);
            toast.error('Failed to load refund policy');
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        const method = refundId ? 'PUT' : 'POST';
        const url    = refundId ? `${API_URL}/${refundId}` : API_URL;
        const payload = {
            hero_title_part1:   refundData.hero.titlePart,
            hero_highlight_word: refundData.hero.highlight,
            hero_title_suffix:  refundData.hero.suffix,
            hero_description:   refundData.hero.description,
            last_updated_date:  refundData.lastUpdated,
            sections: refundData.sections.map(s => ({
                icon_type:     s.icon,
                section_title: s.title,
                content:       s.content,
                key_points:    s.points,
            })),
        };
        try {
            const { ok, data: result } = await safeFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(payload),
            });
            if (ok) {
                if (!refundId) setRefundId(result.id);
                toast.success('Refund Policy updated successfully!');
            } else {
                toast.error('Failed to update refund policy');
            }
        } catch (err) {
            console.error('Error saving refund policy:', err);
            toast.error('Network error while saving');
        } finally {
            setIsSaving(false);
        }
    };

    /* ── Section helpers ── */
    const addSection = () => {
        setRefundData(prev => ({
            ...prev,
            sections: [...prev.sections, {
                id: Date.now(), icon: 'undo',
                title: 'New Section Title',
                content: 'Section content goes here…', points: [],
            }],
        }));
    };

    const removeSection = (id) =>
        setRefundData(prev => ({ ...prev, sections: prev.sections.filter(s => s.id !== id) }));

    const updateSection = (id, field, value) =>
        setRefundData(prev => ({
            ...prev,
            sections: prev.sections.map(s => s.id === id ? { ...s, [field]: value } : s),
        }));

    const addPoint = (sectionId) =>
        setRefundData(prev => ({
            ...prev,
            sections: prev.sections.map(s =>
                s.id === sectionId ? { ...s, points: [...s.points, 'New point'] } : s
            ),
        }));

    const removePoint = (sectionId, idx) =>
        setRefundData(prev => ({
            ...prev,
            sections: prev.sections.map(s =>
                s.id === sectionId ? { ...s, points: s.points.filter((_, i) => i !== idx) } : s
            ),
        }));

    const updatePoint = (sectionId, idx, value) =>
        setRefundData(prev => ({
            ...prev,
            sections: prev.sections.map(s => {
                if (s.id !== sectionId) return s;
                const pts = [...s.points];
                pts[idx] = value;
                return { ...s, points: pts };
            }),
        }));

    const setHero = (field, value) =>
        setRefundData(prev => ({ ...prev, hero: { ...prev.hero, [field]: value } }));

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Edit <span>Refund Policy</span></h1>
                        <p>Manage your refund, cancellation, and dispute resolution statements.</p>
                    </div>
                    <div className="header-actions">
                        <button className="save-btn" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving…' : <><FaSave /> Save Changes</>}
                        </button>
                    </div>
                </header>

                <div className="admin-privacy-container">
                    {/* ── Hero Section ── */}
                    <div className="admin-card-section">
                        <div className="section-header">
                            <h3><FaHeading /> Hero Section</h3>
                        </div>
                        <div className="admin-form-grid">
                            <div className="admin-input-group">
                                <label>Title Part 1</label>
                                <input type="text" value={refundData.hero.titlePart}
                                    onChange={e => setHero('titlePart', e.target.value)} />
                            </div>
                            <div className="admin-input-group">
                                <label>Highlight Word</label>
                                <input type="text" className="highlight-field"
                                    value={refundData.hero.highlight}
                                    onChange={e => setHero('highlight', e.target.value)} />
                            </div>
                            <div className="admin-input-group full-width">
                                <label>Title Suffix</label>
                                <input type="text" value={refundData.hero.suffix}
                                    onChange={e => setHero('suffix', e.target.value)} />
                            </div>
                            <div className="admin-input-group full-width">
                                <label>Description</label>
                                <textarea rows="3" value={refundData.hero.description}
                                    onChange={e => setHero('description', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {/* ── Policy Sections ── */}
                    <div className="sections-list">
                        <div className="sections-header">
                            <h3><FaRegFileAlt /> Policy Sections</h3>
                            <button className="add-section-btn" onClick={addSection}>
                                <FaPlus /> Add New Section
                            </button>
                        </div>

                        <AnimatePresence>
                            {refundData.sections.map((section, index) => (
                                <motion.div
                                    key={section.id}
                                    className="admin-policy-section-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                >
                                    <div className="card-top">
                                        <h4>Section #{index + 1}</h4>
                                        <button className="delete-btn" onClick={() => removeSection(section.id)}>
                                            <FaTrash />
                                        </button>
                                    </div>

                                    <div className="admin-form-grid">
                                        <div className="admin-input-group">
                                            <label>Icon Type</label>
                                            <select value={section.icon}
                                                onChange={e => updateSection(section.id, 'icon', e.target.value)}>
                                                <option value="undo">Undo (Refund)</option>
                                                <option value="shake">Handshake (Agreement)</option>
                                                <option value="warning">Warning (Non-Refundable)</option>
                                                <option value="clock">Clock (Timeline)</option>
                                                <option value="balance">Balance (Legal)</option>
                                                <option value="email">Email (Contact)</option>
                                            </select>
                                        </div>
                                        <div className="admin-input-group">
                                            <label>Section Title</label>
                                            <input type="text" value={section.title}
                                                onChange={e => updateSection(section.id, 'title', e.target.value)} />
                                        </div>
                                        <div className="admin-input-group full-width">
                                            <label><FaParagraph /> Content</label>
                                            <textarea rows="4" value={section.content}
                                                onChange={e => updateSection(section.id, 'content', e.target.value)} />
                                        </div>

                                        <div className="admin-input-group full-width">
                                            <label><FaListUl /> Key Points (Optional)</label>
                                            <div className="points-manager">
                                                {section.points.map((point, pIdx) => (
                                                    <div key={pIdx} className="point-row">
                                                        <input type="text" value={point}
                                                            onChange={e => updatePoint(section.id, pIdx, e.target.value)} />
                                                        <button className="remove-point"
                                                            onClick={() => removePoint(section.id, pIdx)}>
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                ))}
                                                <button className="add-point-btn"
                                                    onClick={() => addPoint(section.id)}>
                                                    <FaPlus /> Add Point
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    {/* ── Footer meta ── */}
                    <div className="admin-card-section footer-meta">
                        <div className="admin-input-group">
                            <label>Last Updated Date</label>
                            <input type="text"
                                value={refundData.lastUpdated}
                                onChange={e => setRefundData(prev => ({ ...prev, lastUpdated: e.target.value }))} />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminRefundPolicy;
